import { useParams } from "react-router-dom"
import styled from "styled-components"

import {
  Banner,
  breakpoint,
  colors,
  spacing,
  Text,
} from "@minuthq/meatball-ui-react"

import { TaskActivityLog } from "src/components/TaskManagement/TaskDetails/TaskActivityLog"
import { TaskMetadata } from "src/components/TaskManagement/TaskDetails/TaskMetadata"
import { TaskStatusDropdown } from "src/components/TaskManagement/TaskStatusDropdown"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchHome } from "src/data/organizations/queries/homeQueries"
import {
  useFetchTask,
  usePatchTask,
  usePutTimeWindow,
} from "src/data/taskManagement/taskManagementQueries"
import { TTask } from "src/data/taskManagement/taskManagementTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { Container } from "src/ui/Container/Container"
import { Divider } from "src/ui/Divider/Divider"
import { Editable } from "src/ui/Editable/Editable"
import { BreadcrumbView } from "src/ui/Layout/BreadcrumbView"
import { MSkeleton } from "src/ui/MSkeleton/MSkeleton"

export function TaskDetails() {
  const { t, langKeys } = useTranslate()
  const { orgId } = useOrganization()
  const { taskId } = useParams<{ taskId: string }>()

  const fetchTask = useFetchTask({
    orgId,
    taskId,
  })

  const task = fetchTask.data

  const fetchHome = useFetchHome({
    orgId,
    homeId: task?.home_id || "",
    options: {
      enabled: !!task?.home_id,
    },
  })

  const home = fetchHome.data

  const loading = fetchTask.isLoading || fetchHome.isInitialLoading
  const error = fetchTask.isError || fetchHome.isError

  const patchTask = usePatchTask()
  const putTimeWindow = usePutTimeWindow()

  async function handleUpdateTask(
    updates: Partial<Omit<TTask, "time_window">>
  ) {
    patchTask.mutate(
      {
        body: updates,
        path: { organization_id: orgId, task_id: taskId },
      },
      {
        onError: (error) => {
          if (
            error.response?.data.error_key ===
            "assignee_does_not_have_access_to_home"
          ) {
            return patchTask.mutate({
              body: { ...updates, assigned_to: { type: "unassigned" } },
              path: { organization_id: orgId, task_id: taskId },
            })
          }
        },
      }
    )
  }

  async function handleUpdateTimeWindow(timeWindow: TTask["time_window"]) {
    // Using PUT here, because we want to be able to unset the earliest start date
    putTimeWindow.mutate({
      body: timeWindow,
      path: { organization_id: orgId, task_id: taskId },
    })
  }

  if (!task && loading) {
    return <MSkeleton width={350} height={48} />
  }

  if (error) {
    return (
      <Banner type="error">
        {t(langKeys.failed_general_error_try_refreshing_page)}
      </Banner>
    )
  }

  return (
    <BreadcrumbView
      title={
        loading ? (
          <MSkeleton width={350} height={48} />
        ) : (
          <Text variant="heading1">
            <Editable
              onSave={(title) => handleUpdateTask({ title })}
              tooltipText={t(
                langKeys.task_management_update_task_title_tooltip
              )}
            >
              {task?.title}
            </Editable>
          </Text>
        )
      }
      breadcrumbs={[
        {
          label: t(langKeys.task_plural),
          to: Routes.TaskManagement.location(),
        },
        {
          label: task?.title ?? "",
          to: Routes.Task.location(taskId),
        },
      ]}
      actionBar={
        task && (
          <TaskStatusDropdown
            currentStatus={task.status}
            onChange={(status) => {
              handleUpdateTask({ status })
            }}
          />
        )
      }
    >
      <StyledContainer>
        <ContentSection>
          <Editable
            placeholder={t(
              langKeys.task_management_update_task_description_tooltip
            )}
            multiline={true}
            onSave={(description) => handleUpdateTask({ description })}
            tooltipText={t(
              langKeys.task_management_update_task_description_tooltip
            )}
          >
            {task?.description}
          </Editable>

          <ResponsiveDivider>
            <Divider />
          </ResponsiveDivider>

          <MobileSidebar>
            <TaskMetadata
              task={task}
              onUpdateTask={handleUpdateTask}
              onUpdateTimeWindow={handleUpdateTimeWindow}
              home={home}
            />
          </MobileSidebar>

          <Divider />

          <TaskActivityLog
            taskId={taskId}
            timezone={home?.timezone ?? ""}
            homeId={home?.home_id ?? ""}
          />
        </ContentSection>
        <DesktopSidebar>
          <TaskMetadata
            task={task}
            onUpdateTask={handleUpdateTask}
            onUpdateTimeWindow={handleUpdateTimeWindow}
            home={home}
          />
        </DesktopSidebar>
      </StyledContainer>
    </BreadcrumbView>
  )
}

const StyledContainer = styled(Container)`
  display: grid;
  gap: ${spacing.XL};

  @container (${breakpoint.mediumUp}) {
    grid-template-columns: 2fr 1fr;

    & > :first-child {
      border-right: 1px solid ${colors.divider};
      padding-right: ${spacing.XL};
    }
  }
`

const ContentSection = styled.div`
  display: grid;
  gap: ${spacing.XL};
`

const MobileSidebar = styled.div`
  display: block;

  @container (${breakpoint.mediumUp}) {
    display: none;
  }
`

const DesktopSidebar = styled.div`
  display: none;

  @container (${breakpoint.mediumUp}) {
    display: block;
  }
`

const ResponsiveDivider = styled.div`
  display: block;

  @container (${breakpoint.mediumUp}) {
    display: none;
  }
`
